<template>
    <div class="flex flex-row gap-10">
        <div class="w-6/12">
            <div class="subheader subheader-border">Campaigns</div>
            <DataTable :loading="loading" selectionMode="single" :value="filteredCampaignData" dataKey="advertiserName"
                scrollable :scrollHeight="'80vh'" v-model:expandedRows="expandedRows"
                v-model:selection="selectedAdvertiser"
                class="border border-gray-300 rounded-xl shadow-sm  md:col-span-1 mt-5" fluid>
                <Column style="width: 5%" expander :headerStyle="headerStyles">

                </Column>
                <Column field="advertiserName" header="Voonix advertiser name" :headerStyle="headerStyles">
                </Column>
                <template #expansion="slotProps">
                    <div class="p-4">
                        <DataTable v-if="getCampaignsByAdvertiser(slotProps.data.advertiserName).value.length > 0"
                            selectionMode="single" v-model:selection="selectedCampaign"
                            :rowClass="campaignRowClass"
                            :value="getCampaignsByAdvertiser(slotProps.data.advertiserName).value">
                            <Column field="voonixCampaignKey" header="Campaign Key" :headerStyle="nestedHeaderStyles"></Column>
                            <Column field="voonixCampaignName" header="Campaign Name" :headerStyle="nestedHeaderStyles"></Column>
                            <Column field="earnings.ftds" header="Ftd's" :headerStyle="nestedHeaderStyles"></Column>
                            <Column field="earnings.depositValue" header="Deposit Value" :headerStyle="nestedHeaderStyles">
                                <template #body="slotProps">
                                    <div >
                                        <p>{{ formatCurrency(slotProps.data.earnings.depositValue) }}</p>
                                    </div>
                                </template>
                                </Column>
                            <Column field="earnings.clicks" header="Clicks" :headerStyle="nestedHeaderStyles"></Column>
                            <Column field="earnings.revenue" header="Revenue" :headerStyle="nestedHeaderStyles">
                                <template #body="slotProps">
                                    <div >
                                        <p>{{ formatCurrency(slotProps.data.earnings.revenue) }}</p>
                                    </div>
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </template>
            </DataTable>
        </div>
        <div class="w-6/12">
            <div v-if="selectedVoonixId !== null" class="subheader-border"> Showing possible site links for campaign
                key: <span class="highlighted-span">
                    {{ selectedCampaign.voonixCampaignKey }}
                </span>
            </div>
            <div v-else class="subheader-border"> Select a campaign key to view possible site links </div>
            <DataTable :value="getSiteLinksByVoonixId().value" dataKey="id" scrollable :rowStyle="siteLinksRowStyle"
                :scrollHeight="'80vh'" :loading="loading" :virtualScrollerOptions="{ itemSize: 100 }"
                class="border border-gray-300 rounded-xl shadow-sm  md:col-span-1 mt-5" fluid>
                <Column field="internalNote" header="Internal Note" :headerStyle="headerStyles"></Column>
                <Column field="siteId.name" header="Site name" :headerStyle="headerStyles"></Column>
                <Column field="deal" header="Deal" :headerStyle="headerStyles">
                    <template #body="slotProps">
                                        {{ getFriendlyDealMessage(slotProps.data.deal) }}
                                    </template>
                    </Column>
                <Column field="affiliateLink" header="Link" :headerStyle="headerStyles"></Column>
                <Column :headerStyle="headerStyles">
                    <template #body="{ data }">
                        <div class="ml-5">
                            <Button :loading="mappingLoading[data._id]" severity="primary" class="h-8 w-3 outline"
                                icon="pi pi-link cursor-pointer" v-tooltip.top="'Map'" aria-label="Map site link"
                                @click="openMapSiteLinkConfirmation($event, data._id)" />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </div>
    </div>

</template>

<script setup>
import { ref, onMounted, reactive, watch, computed } from "vue";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { formatCurrency } from '@/utils/currencies';
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import CampaignService from "@/services/CampaignService";
import SiteLinkService from "@/services/SiteLinkService";
import { getFriendlyDealMessage } from '@/utils/deals';
import { campaignKeyMatch } from '@/utils/campaign-keys';
import { useRoute } from 'vue-router';

const confirm = useConfirm();
const toast = useToast();
const route = useRoute();

const loading = ref(true);
const mappingLoading = ref({});

// {advertiserName: string, campaigns: Array}
const campaignData = ref([]);

const siteLinks = ref(new Array(10));
const selectedCampaign = ref(null);
const selectedVoonixId = ref(null);
const selectedAdvertiser = ref(null);
const expandedRows = ref({});

const props = defineProps({
    searchQuery: String
});

watch(
    () => props.searchQuery,
    (newVal, oldVal) => {
        console.log('searchQuery changed:', newVal);
    }
);

const filteredCampaignData = computed(() => {
    if (!props.searchQuery) return campaignData.value;
    return campaignData.value.filter(campaign =>
        campaign.advertiserName?.toLowerCase().includes(props.searchQuery.toLowerCase())
        // Add more fields if you want to search by more than advertiserName
    );
});

const getCampaignsByAdvertiser = (advertiserName) => {
    return computed(() => {
        // Filter campaignData to find the advertiser by name and return its campaigns
        const advertiser = campaignData.value.find(ad => ad.advertiserName === advertiserName);
        return advertiser ? advertiser.campaigns : [];
    });
};

// Blip the link the campaign key points at. The key equals the internalNote for most
// affiliate systems, but for NetRefer it is the promotion id inside the link (`pid=`)
// and the note never matches — see utils/campaign-keys.js.
const siteLinksRowStyle = (data) => {
    if (selectedCampaign.value && campaignKeyMatch(data, selectedCampaign.value.voonixCampaignKey)) {
        return {
            backgroundColor: '#f1b711', // Background color
            animation: 'blip 1s infinite'  // Apply the blip animation
        };
    }
    return {};  // Return an empty object for rows without special styling
};

const campaignRowClass = (data) => {
    return {
        'selected-row': selectedCampaign.value && selectedCampaign.value._id === data._id
    };
};

const getSiteLinksByVoonixId = () => {
    return computed(() => {
        if (selectedVoonixId.value === null) {
            return [];
        }

        // Filter the site links by voonixAdvertiserId
        let links = siteLinks.value.filter(siteLink => siteLink.voonixAdvertiserId.voonixId === selectedVoonixId.value);

        // Move the links the campaign key points at to the front: an internalNote match
        // first, then a link-pid match (NetRefer), then everything else. `links` is the
        // array filter() just built, so sorting it in place doesn't touch siteLinks.
        if (selectedCampaign.value && selectedCampaign.value.voonixCampaignKey) {
            const rank = (link) => {
                const match = campaignKeyMatch(link, selectedCampaign.value.voonixCampaignKey);
                if (match === 'internalNote') return 0;
                if (match === 'linkPid') return 1;
                return 2;
            };
            links.sort((a, b) => rank(a) - rank(b));
        }

        return links.length ? links : [];
    });
};

watch(selectedCampaign, (newVal) => {
    if (newVal === null) {
        selectedVoonixId.value = null;
        return;
    }
    selectedVoonixId.value = newVal.voonixId;
});

watch(selectedAdvertiser, (newSelection) => {
    if (newSelection) {
        // Expand the selected row by setting its key in expandedRows to true
        expandedRows.value = {
            ...expandedRows.value,
            [newSelection.advertiserName]: true  // Use the dataKey ('advertiserName') as the key
        };
    }
});

function openMapSiteLinkConfirmation(event, siteLinkId) {
    confirm.require({
        target: event.currentTarget,
        message: `Mapping site link to campaign key ${selectedCampaign.value.voonixCampaignKey}`,
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Map'
        },
        accept: () => {
            console.log('Site link mapped');
            mapSiteLink(siteLinkId, selectedAdvertiser.value.advertiserName);
        }
    });
}

async function mapSiteLink(siteLinkId) {
    console.log(`Mapping site link id: ${siteLinkId}, with campaign key: ${selectedCampaign.value._id}`);
    try {
        mappingLoading.value[siteLinkId] = true;
        const response = await SiteLinkService.mapCampaignToSiteBrandLink(selectedCampaign.value._id, siteLinkId);
        let advName = response.data.data.advertiserName;
        // Step 1: Remove the siteLink from the `siteLinks` ref
        siteLinks.value = siteLinks.value.filter(link => link._id !== siteLinkId);
        //remove the campaign by key from the campaignData ref, by camapaign _id from under its advertiser name. if advertiser has 0 campaigns left, remove it as well.
        // Step 2: Remove the campaign from the `campaignData` ref
        campaignData.value = campaignData.value.map(advertiser => {
            if (advertiser.advertiserName === advName) {
                advertiser.campaigns = advertiser.campaigns.filter(campaign => campaign._id !== selectedCampaign.value._id);
                // If no campaigns are left for this advertiser, we will filter out the advertiser as well
                if (advertiser.campaigns.length === 0) {
                    return null; // Mark the advertiser to be removed
                }
            }
            return advertiser; // Return the updated advertiser object
        }).filter(advertiser => advertiser !== null); // Remove advertisers with 0 campaigns
        selectedCampaign.value = null;
        toast.add({ severity: 'success', summary: 'Success', detail: 'Site link mapped successfully', life: 3000 });

    } catch (error) {
        console.error('Error mapping site link:', error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to map site link', life: 3000 });
    }
    mappingLoading.value[siteLinkId] = false;
}

function getUnmappedCampaigns() {
    return CampaignService.getUnmapped().then((response) => {
        campaignData.value = response.data;
    });
}

function getUnmappedSiteLinks() {
    return SiteLinkService.getUnmapped().then((response) => {
        siteLinks.value = response.data;
    });
}

/**
 * Open the page straight onto one campaign from the URL.
 *
 * ?campaignKey=<voonixCampaignKey> (optionally &advertiser=<voonixAdvertiserName>
 * to disambiguate, since a key is only unique within an advertiser) expands the
 * owning advertiser and selects the campaign — which populates the right-hand
 * site-link pane and blips the link whose internalNote matches.
 *
 * Exists so an unmapped-link report can hand over a clickable URL per row
 * instead of "go to /campaigns and search for it yourself".
 *
 * Runs only AFTER both fetches resolve; before that campaignData is empty and
 * there is nothing to select. The previous onMounted fired the two loads
 * without returning their promises, so Promise.all resolved immediately and
 * loading flipped false while the tables were still empty — fixed by returning
 * them, which this depends on.
 */
function applyDeepLink() {
    const wantedKey = route.query.campaignKey;
    if (!wantedKey) return;
    const wantedAdvertiser = route.query.advertiser;

    const advertiser = campaignData.value.find(ad =>
        (!wantedAdvertiser || ad.advertiserName === wantedAdvertiser)
        && (ad.campaigns || []).some(c => String(c.voonixCampaignKey) === String(wantedKey)));

    if (!advertiser) {
        toast.add({
            severity: 'warn',
            summary: 'Campaign not found',
            detail: `No unmapped campaign with key ${wantedKey}${wantedAdvertiser ? ` under ${wantedAdvertiser}` : ''}. It may already be mapped.`,
            life: 5000,
        });
        return;
    }

    const campaign = advertiser.campaigns.find(c => String(c.voonixCampaignKey) === String(wantedKey));
    // Order matters: selecting the advertiser expands its row, selecting the
    // campaign drives selectedVoonixId and therefore the site-link pane.
    selectedAdvertiser.value = advertiser;
    selectedCampaign.value = campaign;
}

onMounted(async () => {
    await Promise.all([
        getUnmappedCampaigns(),
        getUnmappedSiteLinks()
    ]);
    loading.value = false;
    applyDeepLink();
});

</script>

<style scoped>
.highlighted-span {
    background-color: var(--selected-row-color);
    color: white;
    padding: 5px 10px;
    border-radius: 8px;
}
</style>